import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { UserDto, UserStatusEnum } from "../../dto";
import { SpinnerService } from "../spinner/spinner.service";
import { HttpUserService } from "../../services/user/http-user.service";
import { catchError } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { AuthService } from "../../services/auth/auth.service";

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
	const password = control.get('newPassword')?.value;
	const confirm = control.get('confirmPassword')?.value;
	return password && confirm && password !== confirm ? { passwordMismatch: true } : null;
};

@Component({
	selector: 'app-change-password-modal',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './change-password-modal.component.html',
	styleUrls: ['./change-password-modal.component.scss']
})
export class ChangePasswordModalComponent implements OnInit {
	@Input() modalId = 'changePasswordModal';
	user!: UserDto;

	form!: FormGroup;

	constructor(
		private fb: FormBuilder, 
		private httpService: HttpUserService, 
		private spinnerService: SpinnerService,
		private authService: AuthService,
		private toastr: ToastrService) {}

	ngOnInit() {
		this.form = this.fb.group({
			newPassword: ['', Validators.required],
			confirmPassword: ['', Validators.required]
		}, { validators: passwordMatchValidator });
	}

	open(user: UserDto) {
		if (!user) {
			return;
		}
		this.user = user;
		this.form.reset(); // reset previous values & errors

		const modalElement = document.getElementById(this.modalId);
		if (modalElement) {
			const modal = new (window as any).bootstrap.Modal(modalElement, {
				backdrop: 'static', // optional
				keyboard: false
			});
			modal.show();
		}
	}

	save() {
		if (!this.form.valid) {
			return;
		}

		const payload: UserDto = {...this.user, password: this.form.get('newPassword')?.value};
		this.spinnerService.show();
		this.httpService.changePassword(payload)
			.pipe(
				catchError(error => {
					this.spinnerService.hide();
					console.log('Error changing user password', error);
					this.toastr.error('Failed to change password: ' + (error.error ? error.error.error : error.message));
					throw error;
				})
			)
			.subscribe(data => {
				if (data.success) {
					this.refetchUser();
				} else {
					this.toastr.error('Failed to change password. Reason: ' + data.error);
				}
			});
	}

	refetchUser() {
		this.httpService.getUserById(this.user.id!)
			.pipe(
				catchError(error => {
					this.spinnerService.hide();
					console.log('Error reloading user', error);
					this.toastr.error('Failed to reload user: ' + (error.error ? error.error.error : error.message));
					throw error;
				})
			)
			.subscribe(resp => {
				this.spinnerService.hide();				
				if (resp.status === UserStatusEnum.Active) {
					this.toastr.success("Password changed successfully");
					this.authService.setUser(this.user);
					this.closeModal();
				} else {
					this.toastr.error('An error occurred trying to refresh your user data. Please logout and log back in.');
				}
			});
	}

	signOut() {
		this.closeModal();
		this.authService.signOut();
	}

	private closeModal() {
		const modalElement = document.getElementById(this.modalId);
		if (modalElement) {
			const modalInstance = (window as any).bootstrap.Modal.getInstance(modalElement);
			modalInstance?.hide();
		}
	}
}